// Typed wrappers around the per-agent knowledge endpoints. The three-step
// upload (request URL → PUT bytes → confirm) is exposed as `upload`
// rather than three separate calls because the steps must run in order
// and against the same documentId; callers shouldn't be able to skip
// the confirm.

import type {
  ConfirmUploadInput,
  ConfirmUploadResponse,
  KbDocument,
  ListKbDocumentsResponse,
  RequestUploadUrlInput,
  RequestUploadUrlResponse,
} from '@cortex/shared';
import type { ApiClient } from '../api-client.js';

export interface UploadResult {
  document: ConfirmUploadResponse;
  expiresAt: string;
}

export const knowledgeApi = {
  list: (api: ApiClient, agentId: string) =>
    api.get<ListKbDocumentsResponse>(`/v1/agents/${agentId}/knowledge/documents`),

  remove: (api: ApiClient, agentId: string, documentId: string) =>
    api.del<undefined>(`/v1/agents/${agentId}/knowledge/documents/${documentId}`),

  /**
   * Three-step upload composed into one promise:
   *   1. POST /v1/agents/:id/knowledge/upload-url with metadata
   *   2. PUT the bytes to the returned signed URL (raw fetch, no auth header)
   *   3. POST /v1/agents/:id/knowledge/confirm with the documentId
   *
   * Throws on any HTTP failure. The caller decides whether to retry from
   * step 1 (request a fresh URL) or surface the error to the user.
   */
  upload: async (
    api: ApiClient,
    agentId: string,
    file: File,
    sha256: string,
  ): Promise<UploadResult> => {
    const issue = await api.post<RequestUploadUrlResponse>(
      `/v1/agents/${agentId}/knowledge/upload-url`,
      {
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        bytes: file.size,
        sha256,
      } satisfies RequestUploadUrlInput,
    );
    const putRes = await fetch(issue.uploadUrl, {
      method: 'PUT',
      headers: { 'content-type': file.type || 'application/octet-stream' },
      body: file,
    });
    if (!putRes.ok) {
      throw new Error(`upload PUT failed: ${String(putRes.status)} ${putRes.statusText}`);
    }
    const document = await api.post<ConfirmUploadResponse>(
      `/v1/agents/${agentId}/knowledge/confirm`,
      { documentId: issue.documentId } satisfies ConfirmUploadInput,
    );
    return { document, expiresAt: issue.expiresAt };
  },
} as const;

export const knowledgeListQueryKey = (agentId: string) =>
  ['agent', agentId, 'knowledge'] as const;

/**
 * Browser-native SHA-256 over a File. Required by the API to dedup
 * uploads and tie pre-PUT metadata to the bytes that actually arrive.
 */
export async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export type { KbDocument };
